/**
 * Which file manager documents can be appended to an IAP.
 *
 * The bundle is one PDF, so a file only travels with the plan if it can be turned into pages. Mirrors
 * services/coms/src/routes/Forms/iapFileAttachments.ts, and exists so the picker can grey out a file up front rather
 * than letting the user select something that quietly falls out of the export.
 */

const CONVERTIBLE_OFFICE_MIMETYPES = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  'application/rtf',
  'text/plain',
  'text/csv',
  'text/rtf',
]);

/** PNG and JPEG only: nothing else can be embedded on a page. */
const EMBEDDABLE_IMAGE_MIMETYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
]);

const OFFICE_EXTENSIONS = [
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'odt',
  'ods',
  'odp',
  'rtf',
  'txt',
  'csv',
];

export type IapAttachmentKind = 'pdf' | 'image' | 'office' | 'unsupported';

/** How a file would reach the bundle, decided on mimetype with the extension as a fallback. */
export function classifyIapAttachment(
  mimetype: string | null | undefined,
  fileName: string
): IapAttachmentKind {
  const type = (mimetype ?? '').toLowerCase().split(';')[0].trim();
  if (type === 'application/pdf') return 'pdf';
  if (EMBEDDABLE_IMAGE_MIMETYPES.has(type)) return 'image';
  if (CONVERTIBLE_OFFICE_MIMETYPES.has(type)) return 'office';

  // Uploads predating the mimetype column, and browsers that send
  // application/octet-stream for an office document, still have their name.
  const extension = fileName.toLowerCase().split('.').pop() ?? '';
  if (extension === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg'].includes(extension)) return 'image';
  if (OFFICE_EXTENSIONS.includes(extension)) return 'office';
  return 'unsupported';
}

export function canAppendToIap(
  mimetype: string | null | undefined,
  fileName: string
): boolean {
  return classifyIapAttachment(mimetype, fileName) !== 'unsupported';
}

/** What the picker says next to a file it will not let the user attach. */
export const UNSUPPORTED_ATTACHMENT_REASON =
  'Cannot be converted to PDF pages';
